import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, visitsAPI, billsAPI, inventoryAPI, reportsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowRight,
  AlertCircle,
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Pill,
  Clock,
  Receipt,
  Calendar,
  DollarSign,
  Activity,
  CheckCircle,
  Users,
  UserCheck,
  CreditCard,
  Banknote,
  Smartphone
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { OnShiftTodayCard } from '../components/OnShiftTodayCard';

const Dashboard = () => {
  const { user, isClinicAdmin, isDoctor, isFrontDesk, isInventoryManager, workingLocation, clinic } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [pendingBills, setPendingBills] = useState([]);
  const [inventoryStats, setInventoryStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [expirationAlerts, setExpirationAlerts] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [monthlyPayments, setMonthlyPayments] = useState([]);
  const [closingReport, setClosingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const fetchingRef = React.useRef(false);

  useEffect(() => {
    // Skip fetch until LocationSelector has set the working location
    if (!workingLocation?.id) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    fetchDashboardData().finally(() => {
      fetchingRef.current = false;
      setHasFetched(true);
    });
  }, [workingLocation?.id]);

  // Auto-refresh closing report for Front Desk: on window focus + every 30s
  useEffect(() => {
    if (!isFrontDesk() || !workingLocation?.id) return;
    const fetchClosing = async () => {
      try {
        // Use clinic timezone to determine today's date (not UTC)
        const tz = clinic?.timezone || 'America/Chicago';
        const today = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());
        const res = await reportsAPI.getDailyClosing(today, workingLocation.id);
        setClosingReport(res.data);
      } catch { /* ignore */ }
    };
    fetchClosing();
    const interval = setInterval(fetchClosing, 30000);
    const onFocus = () => fetchClosing();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [isFrontDesk, workingLocation?.id, clinic?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      const locationParams = workingLocation?.id ? { location_id: workingLocation.id } : {};
      
      if (isInventoryManager()) {
        const [inventoryRes, lowStockRes, alertsRes, transactionsRes] = await Promise.all([
          inventoryAPI.getAll(locationParams),
          inventoryAPI.getAll({ ...locationParams, low_stock: true }),
          inventoryAPI.getExpirationAlerts(90).catch(() => ({ data: [] })),
          inventoryAPI.getTransactions({ limit: 10 }).catch(() => ({ data: [] }))
        ]);
        
        // Handle paginated responses
        const invData = inventoryRes.data.items || inventoryRes.data;
        const items = Array.isArray(invData) ? invData : [];
        const activeItems = items.filter(i => i.is_active !== false);
        
        const lowStockData = lowStockRes.data.items || lowStockRes.data;
        const lowStock = Array.isArray(lowStockData) ? lowStockData : [];
        
        const expiringData = alertsRes.data.items || alertsRes.data;
        const expiring = Array.isArray(expiringData) ? expiringData : [];
        
        const transactionsData = transactionsRes.data.items || transactionsRes.data;
        const transactions = Array.isArray(transactionsData) ? transactionsData : [];
        
        // Calculate performance metrics
        const totalItems = activeItems.length;
        const wellStockedItems = totalItems - lowStock.length;
        const stockHealthPercent = totalItems > 0 ? Math.round((wellStockedItems / totalItems) * 100) : 100;
        const nonExpiring = totalItems - expiring.length;
        const expirationHealthPercent = totalItems > 0 ? Math.round((nonExpiring / totalItems) * 100) : 100;
        
        setInventoryStats({
          total_items: totalItems,
          low_stock_count: lowStock.length,
          total_value: items.reduce((sum, i) => sum + (i.unit_price * (i.quantity_on_hand || i.quantity || 0)), 0),
          expiring_soon: expiring.length,
          stock_health: stockHealthPercent,
          expiration_health: expirationHealthPercent,
          overall_score: Math.round((stockHealthPercent + expirationHealthPercent) / 2)
        });
        setLowStockItems(lowStock.slice(0, 5));
        setExpirationAlerts(expiring.slice(0, 5));
        setRecentTransactions(transactions.slice(0, 5));
      } else {
        // Dashboard for non-inventory managers
        // Use dashboard/stats for counts (uses count queries - no full data fetch)
        // Only fetch the minimal data needed for display
        const [statsRes, recentVisitsRes, pendingBillsRes] = await Promise.all([
          dashboardAPI.getStats(locationParams),
          // Fetch recent visits across all active statuses for Recent Activity display
          visitsAPI.getAll({ 
            ...locationParams, 
            status: 'checked_in,in_progress,pending_billing',
            page: 1,
            page_size: 10 
          }),
          // Only fetch 5 pending bills for display
          billsAPI.getAll({ 
            ...locationParams,
            status: 'pending',
            page: 1,
            page_size: 5
          })
        ]);
        
        setStats(statsRes.data);
        
        // Handle paginated responses for recent visits
        const visitsData = recentVisitsRes.data.items || recentVisitsRes.data;
        let visits = Array.isArray(visitsData) ? visitsData : [];
        
        // Sort by most recent activity (updated_at or created_at)
        visits = visits.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at);
          const dateB = new Date(b.updated_at || b.created_at);
          return dateB - dateA; // Most recent first
        }).slice(0, 5); // Take top 5
        
        setRecentVisits(visits);
        
        // Handle paginated responses for pending bills
        const billsData = pendingBillsRes.data.items || pendingBillsRes.data;
        const pending = Array.isArray(billsData) ? billsData : [];
        setPendingBills(pending);
        
        // Monthly payments chart data comes from stats API
        // If stats has monthly_payments, use it; otherwise set empty
        if (statsRes.data.monthly_payments) {
          setMonthlyPayments(statsRes.data.monthly_payments);
        } else {
          setMonthlyPayments([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate grade based on percentage
  const getGrade = (percent) => {
    if (percent >= 95) return 'A';
    if (percent >= 85) return 'B';
    if (percent >= 75) return 'C';
    if (percent >= 65) return 'D';
    return 'F';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  // ==================== INVENTORY MANAGER DASHBOARD ====================
  if (isInventoryManager()) {
    const overallScore = inventoryStats?.overall_score || 100;
    const grade = getGrade(overallScore);
    
    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {format(new Date(), 'EEEE, MMMM d, yyyy')} • {workingLocation?.name || 'All Locations'} • Inventory Management
          </p>
        </div>

        {/* Main Grid - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Overall Performance Card */}
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-slate-800">Overall Performance</h2>
                <Link to="/inventory">
                  <ArrowRight className="w-5 h-5 text-teal-600 cursor-pointer hover:text-teal-700" />
                </Link>
              </div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-6">INVENTORY HEALTH</p>
              
              {/* Overall Score */}
              <div className="mb-6">
                <p className="text-sm text-slate-600 mb-1">Overall Score</p>
                <p className="text-5xl font-bold text-teal-600">
                  {grade} <span className="text-4xl">{overallScore}%</span>
                </p>
              </div>
              
              {/* Metrics */}
              <div className="space-y-0">
                <div className="py-4 bg-slate-50 px-4 -mx-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Stock Health</span>
                    <span className="font-bold text-slate-900">{inventoryStats?.stock_health || 100}%</span>
                  </div>
                </div>
                <div className="py-4 bg-white px-4 -mx-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Expiration Safety</span>
                    <span className="font-bold text-slate-900">{inventoryStats?.expiration_health || 100}%</span>
                  </div>
                </div>
                <div className="py-4 bg-slate-50 px-4 -mx-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Total Items</span>
                    <span className="font-bold text-slate-900">{inventoryStats?.total_items || 0}</span>
                  </div>
                </div>
                <div className="py-4 bg-white px-4 -mx-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Inventory Value</span>
                    <span className="font-bold text-slate-900">${(inventoryStats?.total_value || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts Card */}
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-slate-800">Alerts</h2>
                <Link to="/inventory">
                  <ArrowRight className="w-5 h-5 text-teal-600 cursor-pointer hover:text-teal-700" />
                </Link>
              </div>
              
              {/* Alert Stats */}
              <div className="flex gap-8 mb-6">
                <div>
                  <p className="text-3xl font-bold text-slate-900">{inventoryStats?.low_stock_count || 0}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">LOW STOCK</p>
                </div>
                <div>
                  <p className={`text-3xl font-bold ${inventoryStats?.expiring_soon > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                    {inventoryStats?.expiring_soon || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">EXPIRING SOON</p>
                </div>
              </div>
              
              {/* Low Stock Items List */}
              <div className="space-y-0">
                <p className="text-sm font-medium text-slate-700 mb-3">Items Needing Attention</p>
                {lowStockItems.length === 0 && expirationAlerts.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-teal-200 mb-3" />
                    <p className="text-slate-500 text-sm">All items are well stocked</p>
                  </div>
                ) : (
                  <>
                    {lowStockItems.map((item, idx) => (
                      <div key={item.id} className={`py-3 px-4 -mx-4 ${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{item.name}</p>
                            <p className="text-xs text-slate-500">SKU: {item.sku}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-amber-600">{item.quantity_on_hand || item.quantity || 0}</p>
                            <p className="text-xs text-slate-400">Min: {item.reorder_threshold || 10}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
              
              {/* Action Banner */}
              {(inventoryStats?.low_stock_count > 0 || inventoryStats?.expiring_soon > 0) && (
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800 flex-1">
                      <span className="font-semibold">{inventoryStats?.low_stock_count || 0} items</span> need restocking
                    </p>
                    <Link to="/inventory">
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                        View All
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Recent Activity</h2>
              <Link to="/inventory">
                <ArrowRight className="w-5 h-5 text-teal-600 cursor-pointer hover:text-teal-700" />
              </Link>
            </div>
            {recentTransactions.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 mx-auto text-slate-200 mb-3" />
                <p className="text-slate-500 text-sm">No recent activity</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentTransactions.map((t) => (
                  <div key={t.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        t.action_type === 'add' ? 'bg-teal-100' : 
                        t.action_type === 'consume' ? 'bg-slate-200' : 'bg-blue-100'
                      }`}>
                        {t.action_type === 'add' ? (
                          <TrendingUp className="w-5 h-5 text-teal-600" />
                        ) : t.action_type === 'consume' ? (
                          <TrendingDown className="w-5 h-5 text-slate-600" />
                        ) : (
                          <Activity className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{t.item_name}</p>
                        <p className="text-xs text-slate-500">{t.performed_by_name}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-bold ${t.action_type === 'consume' ? 'text-slate-600' : 'text-teal-600'}`}>
                          {t.action_type === 'consume' ? '-' : '+'}{t.quantity}
                        </p>
                        <p className="text-xs text-slate-400 capitalize">{t.action_type}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== DOCTOR DASHBOARD - iPad App Style ====================
  if (isDoctor()) {
    // Count doctor's patients by status
    const myCheckedIn = recentVisits.filter(v => v.status === 'checked_in' && v.doctor_id === user?.id).length;
    const myInProgress = recentVisits.filter(v => v.status === 'in_progress' && v.doctor_id === user?.id).length;
    const myPatients = recentVisits.filter(v => 
      (v.status === 'checked_in' || v.status === 'in_progress') && v.doctor_id === user?.id
    );

    return (
      <div className="min-h-screen bg-slate-50 -m-6 p-6" data-testid="doctor-dashboard">
        {/* App-like Header */}
        <div className="text-center mb-8">
          <p className="text-slate-500 text-sm">{format(new Date(), 'EEEE, MMMM d')}</p>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">
            My Patients
          </h1>
          <p className="text-slate-500 mt-1">{workingLocation?.name}</p>
        </div>

        {/* Patient Count Cards - Large Touch Targets */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Link to="/visits">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 active:scale-95 transition-transform">
              <div className="flex items-center justify-center w-14 h-14 bg-teal-100 rounded-2xl mx-auto mb-4">
                <UserCheck className="w-7 h-7 text-teal-600" />
              </div>
              <p className={`text-4xl font-bold text-center ${myCheckedIn > 0 ? 'text-rose-600' : 'text-slate-900'}`}>
                {myCheckedIn}
              </p>
              <p className="text-slate-500 text-center mt-1">Waiting</p>
            </div>
          </Link>
          
          <Link to="/visits">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 active:scale-95 transition-transform">
              <div className="flex items-center justify-center w-14 h-14 bg-amber-100 rounded-2xl mx-auto mb-4">
                <Clock className="w-7 h-7 text-amber-600" />
              </div>
              <p className={`text-4xl font-bold text-center ${myInProgress > 0 ? 'text-rose-600' : 'text-slate-900'}`}>
                {myInProgress}
              </p>
              <p className="text-slate-500 text-center mt-1">In Progress</p>
            </div>
          </Link>
        </div>

        {/* Quick Action - Start Seeing Patients */}
        <Link to="/visits">
          <div className="bg-teal-600 rounded-2xl p-5 mb-8 flex items-center justify-between active:bg-teal-700 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-lg">View All Patients</p>
                <p className="text-teal-100 text-sm">{myPatients.length} patients assigned to you</p>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 text-white" />
          </div>
        </Link>

        {/* Patient List - Large Touch Cards */}
        <div className="space-y-3">
          <p className="text-slate-500 text-sm font-medium px-1">TODAY'S QUEUE</p>
          
          {myPatients.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-slate-300" />
              </div>
              <p className="text-slate-900 font-medium">No patients waiting</p>
              <p className="text-slate-500 text-sm mt-1">Your queue is empty</p>
            </div>
          ) : (
            myPatients.slice(0, 5).map((visit) => (
              <Link key={visit.id} to="/visits">
                <div className="bg-white rounded-2xl p-4 flex items-center gap-4 shadow-sm border border-slate-100 active:bg-slate-50 transition-colors">
                  <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-semibold text-slate-600">
                      {visit.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-lg truncate">{visit.patient_name}</p>
                    <p className="text-slate-500 text-sm">
                      {visit.services?.length || 0} services • {visit.medications?.length || 0} meds
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      visit.status === 'checked_in' 
                        ? 'bg-teal-100 text-teal-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {visit.status === 'checked_in' ? 'Waiting' : 'In Progress'}
                    </span>
                    <span className="text-xs text-slate-400">
                      {new Date(visit.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>

        {/* Today's Stats */}
        <div className="mt-8 bg-white rounded-2xl p-5">
          <p className="text-slate-500 text-sm font-medium mb-4">TODAY'S SUMMARY</p>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{stats?.today_visits || 0}</p>
              <p className="text-xs text-slate-500">Patients Seen</p>
            </div>
            <div className="text-center border-x border-slate-100">
              <p className="text-2xl font-bold text-slate-900">{myCheckedIn + myInProgress}</p>
              <p className="text-xs text-slate-500">In Queue</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-teal-600">{stats?.completed_visits || 0}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== FRONT DESK / ADMIN DASHBOARD ====================
  // Use stats from API for accurate counts
  const checkedInCount = stats?.waiting_patients || 0;
  const inProgressCount = (stats?.in_progress || 0) + (stats?.pending_billing || 0);
  const pendingBillingCount = stats?.pending_billing || 0;
  const completedCount = recentVisits.filter(v => v.status === 'completed').length;
  
  const totalRevenue = stats?.total_revenue || 0;
  const pendingAmount = pendingBills.reduce((sum, b) => sum + (b.balance_due || 0), 0);
  const todayRevenue = stats?.today_revenue || 0;
  const clinicTz = clinic?.timezone || 'America/Chicago';
  const clinicToday = new Intl.DateTimeFormat('en-CA', { timeZone: clinicTz }).format(new Date());

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.full_name?.split(' ')[0]}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {format(new Date(), 'EEEE, MMMM d, yyyy')} • {workingLocation?.name || 'All Locations'}
        </p>
      </div>

      {/* Main Grid - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Patients Needing Attention - Left Column (2/3 width) */}
        <Card className="border-0 shadow-sm bg-white lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Patients Needing Attention</h2>
            </div>
            
            {/* Status Tabs - Modern Design */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Link to="/visits?tab=checked_in" className="group">
                <div className="relative overflow-hidden bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-slate-300 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-3xl font-bold ${checkedInCount > 0 ? 'text-rose-600' : 'text-slate-900'}`}>{checkedInCount}</p>
                      <p className="text-sm text-slate-500 mt-1">Checked In</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-slate-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-rose-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                </div>
              </Link>
              
              <Link to="/visits?tab=in_progress" className="group">
                <div className="relative overflow-hidden bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-slate-300 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-3xl font-bold ${inProgressCount > 0 ? 'text-rose-600' : 'text-slate-900'}`}>{inProgressCount}</p>
                      <p className="text-sm text-slate-500 mt-1">In Progress</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-slate-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-rose-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                </div>
              </Link>
              
              <Link to="/billing?tab=pending" className="group">
                <div className="relative overflow-hidden bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-slate-300 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-3xl font-bold ${pendingBillingCount > 0 ? 'text-rose-600' : 'text-slate-900'}`}>{pendingBillingCount}</p>
                      <p className="text-sm text-slate-500 mt-1">Pending Billing</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Receipt className="w-6 h-6 text-slate-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-rose-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                </div>
              </Link>
            </div>

            {/* Recent Visits List */}
            <div className="space-y-0">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-slate-700">Recent Activity</p>
                <Link to="/visits" className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {recentVisits.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-slate-200 mb-3" />
                  <p className="text-slate-500 text-sm">No active visits</p>
                </div>
              ) : (
                <>
                  {recentVisits.slice(0, 5).map((visit, idx) => {
                    // Format time for display
                    const activityTime = new Date(visit.updated_at || visit.created_at);
                    const timeStr = activityTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                    
                    return (
                      <div key={visit.id} className={`py-3 px-4 -mx-4 ${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                              visit.status === 'checked_in' ? 'bg-teal-100' :
                              visit.status === 'in_progress' ? 'bg-amber-100' :
                              visit.status === 'pending_billing' ? 'bg-rose-100' :
                              'bg-slate-200'
                            }`}>
                              <span className={`text-sm font-medium ${
                                visit.status === 'checked_in' ? 'text-teal-700' :
                                visit.status === 'in_progress' ? 'text-amber-700' :
                                visit.status === 'pending_billing' ? 'text-rose-700' :
                                'text-slate-600'
                              }`}>
                                {visit.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{visit.patient_name}</p>
                              <p className="text-xs text-slate-500">
                                {visit.doctor_name || 'Unassigned'} • {timeStr}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              visit.status === 'checked_in' ? 'bg-teal-100 text-teal-700' :
                              visit.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                              visit.status === 'pending_billing' ? 'bg-rose-100 text-rose-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {visit.status === 'pending_billing' ? 'ready for billing' : visit.status.replace(/_/g, ' ')}
                            </span>
                            <Link to={`/visits?tab=${visit.status === 'in_progress' || visit.status === 'pending_billing' ? 'in_progress' : visit.status === 'completed' ? 'completed' : 'checked_in'}`}>
                              <Button variant="outline" size="sm" className="text-xs">View</Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right Column - Quick Stats */}
        <div className="space-y-6">
          {/* Quick Actions Card */}
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 gap-3">
                <Link to="/patients">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 transition-all cursor-pointer group">
                    <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Check In Patient</p>
                      <p className="text-xs text-slate-500">Register patient arrival</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                  </div>
                </Link>
                <Link to="/billing">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 transition-all cursor-pointer group">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Process Payment</p>
                      <p className="text-xs text-slate-500">Handle billing & payments</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                  </div>
                </Link>
                <Link to="/visits">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 transition-all cursor-pointer group">
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">View Visits</p>
                      <p className="text-xs text-slate-500">Monitor patient flow</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* On Shift Today — temporarily hidden from UI (backend + component kept intact for later use) */}
          {false && (isFrontDesk() || isClinicAdmin()) && (
            <OnShiftTodayCard
              locationId={workingLocation?.id}
              locationName={workingLocation?.name}
              date={clinicToday}
            />
          )}
        </div>
      </div>

      {/* Bottom Row - Performance & Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Today's Performance */}
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-slate-800">Today's Performance</h2>
            </div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-4">DAILY METRICS</p>
            
            {/* Metrics Grid */}
            <div className="space-y-0">
              <div className="py-4 bg-slate-50 px-4 -mx-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Patients Seen</span>
                  <span className="font-bold text-slate-900">{stats?.today_visits || 0}</span>
                </div>
              </div>
              <div className="py-4 bg-white px-4 -mx-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Currently Waiting</span>
                  <span className="font-bold text-slate-900">{stats?.waiting_patients || 0}</span>
                </div>
              </div>
              <div className="py-4 bg-slate-50 px-4 -mx-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Revenue Today</span>
                  <span className="font-bold text-teal-600">${todayRevenue.toLocaleString()}</span>
                </div>
              </div>
              <div className="py-4 bg-white px-4 -mx-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Pending Bills</span>
                  <span className="font-bold text-slate-900">{stats?.pending_bills || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payments Card */}
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-slate-800">Payments</h2>
              <Link to="/billing">
                <ArrowRight className="w-5 h-5 text-slate-400 cursor-pointer hover:text-slate-600" />
              </Link>
            </div>
            
            {/* Payment Stats */}
            <div className="flex gap-8 mb-6">
              <div>
                <p className="text-3xl font-bold text-teal-600">${todayRevenue.toLocaleString()}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">TODAY (COLLECTED)</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">${pendingAmount.toLocaleString()}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">PENDING BALANCE</p>
              </div>
            </div>
            
            {/* Monthly Chart - hidden for Front Desk */}
            {!isFrontDesk() && (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyPayments}>
                  <XAxis 
                    dataKey="period" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 11 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="amount" 
                    fill="#14b8a6" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            )}

            {/* Payment Breakdown - Front Desk only */}
            {isFrontDesk() && closingReport && (
            <div>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-slate-700">Cash</span>
                  </div>
                  <span className="font-semibold text-green-600">${(closingReport.payment_totals?.cash || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-slate-700">Credit/Debit Card</span>
                  </div>
                  <span className="font-semibold text-blue-600">${(closingReport.payment_totals?.credit_card || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-slate-700">Zelle / CashApp</span>
                  </div>
                  <span className="font-semibold text-purple-600">${(closingReport.payment_totals?.zelle_cashapp || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                {closingReport.payment_totals?.other > 0 && (
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Other</span>
                  </div>
                  <span className="font-semibold text-slate-700">${(closingReport.payment_totals?.other || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                )}
              </div>
            </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Doctor's Queue - Only for doctors */}
      {isDoctor() && (
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">My Queue</h2>
              <Link to="/visits">
                <ArrowRight className="w-5 h-5 text-slate-400 cursor-pointer hover:text-slate-600" />
              </Link>
            </div>
            {recentVisits.filter(v => v.status === 'checked_in').length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto text-slate-200 mb-3" />
                <p className="text-slate-500 text-sm">No patients waiting</p>
              </div>
            ) : (
              <div className="space-y-0">
                {recentVisits.filter(v => v.status === 'checked_in').slice(0, 5).map((visit, idx) => (
                  <div key={visit.id} className={`py-3 px-4 -mx-4 ${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{visit.patient_name}</p>
                        <p className="text-xs text-slate-500">Waiting since check-in</p>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        waiting
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
